import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import type { CreateLeadInput } from '@/api/leads';
import type { LeadDetail } from '@/types';

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  phone: z.string().min(10, 'Valid phone required'),
  email: z.string().email().optional().or(z.literal('')),
  age: z.string().optional(),
  disease: z.string().optional(),
  treatment: z.string().optional(),
  location: z.string().optional(),
  city: z.string().optional(),
  category: z.string().optional(),
  source: z.enum(['meta', 'google', 'seo', 'gmb', 'referral', 'walk_in', 'call', 'website', 'other']).optional(),
  dueDate: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

interface CreateLeadModalProps {
  onClose: () => void;
  onSubmit: (data: CreateLeadInput) => void;
  isLoading?: boolean;
}

const CATEGORY_OPTIONS = [
  { value: 'OPD', label: 'OPD' },
  { value: 'IPD', label: 'IPD' },
  { value: 'Emergency', label: 'Emergency' },
  { value: 'General', label: 'General' },
];

const SOURCE_OPTIONS = [
  { value: 'meta', label: 'Meta (Facebook)' },
  { value: 'google', label: 'Google' },
  { value: 'seo', label: 'SEO' },
  { value: 'gmb', label: 'Google My Business' },
  { value: 'referral', label: 'Referral' },
  { value: 'walk_in', label: 'Walk-in' },
  { value: 'call', label: 'Call' },
  { value: 'website', label: 'Website' },
  { value: 'other', label: 'Other' },
];

export function CreateLeadModal({ onClose, onSubmit, isLoading }: CreateLeadModalProps) {
  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onFormSubmit = (data: FormData) => {
    onSubmit({
      ...data,
      email: data.email || undefined,
    } as CreateLeadInput);
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Create New Lead"
      size="xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button form="create-lead-form" type="submit" loading={isLoading}>Create Lead</Button>
        </>
      }
    >
      <form id="create-lead-form" onSubmit={handleSubmit(onFormSubmit)} className="grid grid-cols-2 gap-4">
        {/* PHI notice */}
        <div className="col-span-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
          🔒 Patient information is encrypted and HIPAA protected. All access is logged.
        </div>

        <Input {...register('name')} label="Full Name" error={errors.name?.message} required placeholder="Patient full name" />
        <Input {...register('phone')} label="Phone Number" error={errors.phone?.message} required placeholder="+91 XXXXX XXXXX" type="tel" />
        <Input {...register('email')} label="Email" error={errors.email?.message} placeholder="patient@email.com" type="email" />
        <Input {...register('age')} label="Age" placeholder="e.g. 35" />
        <Input {...register('disease')} label="Disease / Condition" placeholder="e.g. Piles, Kidney Stone" />
        <Input {...register('treatment')} label="Treatment" placeholder="e.g. Surgery, Medication" />
        <Input {...register('location')} label="Location" placeholder="City / Area" />
        <Input {...register('city')} label="City" placeholder="City name" />
        <Input {...register('category')} label="Category" placeholder="e.g. OPD, IPD, Emergency" />
        <Select
          {...register('source')}
          label="Lead Source"
          options={SOURCE_OPTIONS}
          placeholder="Select source"
        />
        <Input {...register('dueDate')} label="Due Date" type="date" />
      </form>
    </Modal>
  );
}

// ─── Edit Lead Modal ──────────────────────────────────────────────────────────

const editSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  phone: z.string().min(10, 'Valid phone required'),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  age: z.string().optional(),
  disease: z.string().optional(),
  treatment: z.string().optional(),
  location: z.string().optional(),
  city: z.string().optional(),
  category: z.string().optional(),
  source: z.enum(['meta', 'google', 'seo', 'gmb', 'referral', 'walk_in', 'call', 'website', 'other']).optional(),
  dueDate: z.string().optional(),
  opdDate: z.string().optional(),
  ipdDate: z.string().optional(),
  insuranceProvider: z.string().optional(),
  policyNumber: z.string().optional(),
});

type EditFormData = z.infer<typeof editSchema>;

interface EditLeadModalProps {
  lead: LeadDetail;
  onClose: () => void;
  onSubmit: (data: EditFormData) => void;
  isLoading?: boolean;
}

export function EditLeadModal({ lead, onClose, onSubmit, isLoading }: EditLeadModalProps) {
  const { register, handleSubmit, formState: { errors, isDirty } } = useForm<EditFormData>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      name: lead.name ?? '',
      phone: lead.phone ?? '',
      email: lead.email ?? '',
      age: lead.age ?? '',
      disease: lead.disease ?? '',
      treatment: lead.treatment ?? '',
      location: lead.location ?? '',
      city: lead.city ?? '',
      category: lead.category ?? '',
      source: lead.source ?? undefined,
      dueDate: lead.dueDate ? lead.dueDate.split('T')[0] : '',
      opdDate: lead.opdDate ? lead.opdDate.split('T')[0] : '',
      ipdDate: lead.ipdDate ? lead.ipdDate.split('T')[0] : '',
      insuranceProvider: lead.insuranceProvider ?? '',
      policyNumber: lead.policyNumber ?? '',
    },
  });

  return (
    <Modal isOpen onClose={onClose} title="Edit Lead" size="2xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button form="edit-lead-form" type="submit" loading={isLoading} disabled={!isDirty}>
            Save Changes
          </Button>
        </>
      }>
      <form id="edit-lead-form" onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* PHI notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-xs text-amber-700 flex items-center gap-2">
          🔒 Patient information is encrypted and HIPAA protected. All access is logged.
        </div>

        {/* Section: Personal */}
        <div>
          <p className="text-xs font-bold text-[#1e3a5f] uppercase tracking-wider mb-3 pb-1.5 border-b border-slate-100">
            Personal Information
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Input {...register('name')} label="Full Name" error={errors.name?.message} required placeholder="Patient full name" />
            <Input {...register('phone')} label="Phone Number" error={errors.phone?.message} required placeholder="+91 XXXXX XXXXX" type="tel" />
            <Input {...register('email')} label="Email" error={errors.email?.message} placeholder="patient@email.com" type="email" />
            <Input {...register('age')} label="Age" placeholder="e.g. 35" />
            <Input {...register('location')} label="Location / Area" placeholder="e.g. Banjara Hills" />
            <Input {...register('city')} label="City" placeholder="e.g. Hyderabad" />
          </div>
        </div>

        {/* Section: Medical */}
        <div>
          <p className="text-xs font-bold text-[#1e3a5f] uppercase tracking-wider mb-3 pb-1.5 border-b border-slate-100">
            Medical Details
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Input {...register('disease')} label="Disease / Condition" placeholder="e.g. Piles, Kidney Stone" />
            <Input {...register('treatment')} label="Treatment" placeholder="e.g. Surgery, Medication" />
            <Select {...register('category')} label="Category" options={CATEGORY_OPTIONS} placeholder="Select category" />
            <Select {...register('source')} label="Lead Source" options={SOURCE_OPTIONS} placeholder="Select source" />
          </div>
        </div>

        {/* Section: Dates */}
        <div>
          <p className="text-xs font-bold text-[#1e3a5f] uppercase tracking-wider mb-3 pb-1.5 border-b border-slate-100">
            Dates
          </p>
          <div className="grid grid-cols-3 gap-3">
            <Input {...register('dueDate')} label="Follow-up Due Date" type="date" />
            <Input {...register('opdDate')} label="OPD Date" type="date" />
            <Input {...register('ipdDate')} label="IPD Date" type="date" />
          </div>
        </div>

        {/* Section: Insurance */}
        {(lead.isInsuranceCase) && (
          <div>
            <p className="text-xs font-bold text-[#1e3a5f] uppercase tracking-wider mb-3 pb-1.5 border-b border-slate-100">
              Insurance Details
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Input {...register('insuranceProvider')} label="Insurance Provider" placeholder="e.g. Star Health" />
              <Input {...register('policyNumber')} label="Policy Number" placeholder="Policy / TPA number" />
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
}
